// src/SalesmanReport.js
import React, { useEffect, useState } from "react";
import { database, ref, onValue } from "./firebase";
import { products } from "./salesmanHelpers";
import { useNavigate } from "react-router-dom";

export default function SalesmanReport() {
  const [customerData, setCustomerData] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const navigate = useNavigate();

  const salesmanPhone = localStorage.getItem("phone");
  const salesmanName = localStorage.getItem("name") || "Salesman";

  useEffect(() => {
    if (!salesmanPhone) {
      setCustomerData([]);
      setRoutes([]);
      return;
    }

    const invoicesRef = ref(database, `salesmanInvoices/${salesmanPhone}`);
    onValue(invoicesRef, (snap) => {
      if (!snap.exists()) {
        setCustomerData([]);
        setRoutes([]);
        return;
      }

      const invoices = Object.values(snap.val());
      const customerMap = {};
      const routeSet = new Set();

      invoices.forEach((inv) => {
        const name = inv.customerName || "Unknown";
        const cphone = inv.customerPhone || "N/A";
        const route = inv.route || "Unassigned";
        routeSet.add(route);

        // Date filtering
        if (fromDate || toDate) {
          const invDate = inv.date ? new Date(inv.date) : null;
          if (invDate) {
            if (fromDate && invDate < new Date(fromDate)) return;
            if (toDate && invDate > new Date(toDate)) return;
          }
        }

        // Route filtering
        if (selectedRoute && route !== selectedRoute) return;

        if (!customerMap[cphone]) {
          customerMap[cphone] = {
            name,
            phone: cphone,
            route,
            totals: Array(products.length).fill(0),
            schemes: Array(products.length).fill(0),
          };
        }

        inv.items?.forEach((item) => {
          const idx = products.indexOf(item.product);
          if (idx !== -1) {
            customerMap[cphone].totals[idx] += item.quantity || 0;
            customerMap[cphone].schemes[idx] += item.scheme || 0;
          }
        });
      });

      setCustomerData(Object.values(customerMap));
      setRoutes(Array.from(routeSet));
    });
  }, [salesmanPhone, selectedRoute, fromDate, toDate]);

  if (!salesmanPhone) {
    return (
      <div style={{ padding: "20px" }}>
        <h2>Salesman Report</h2>
        <p>No salesman found. Please login again.</p>
        <button onClick={() => navigate(-1)}>Back</button>
      </div>
    );
  }

  // Short product names
  const shortName = (p) => {
    if (p.includes("Premium 1 Litre")) return "Pink 1L";
    if (p.includes("Green 1 Litre")) return "Green 1L";
    if (p.includes("Premium 2 Litre")) return "Pink 2L";
    if (p.includes("Green 2 Litre")) return "Green 2L";
    if (p.includes("Premium 500ML")) return "Pink 500ml";
    if (p.includes("Green 500ML")) return "Green 500ml";
    if (p.includes("Premium 250ML")) return "Pink 250ml";
    return p;
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>📊 Salesman Report - {salesmanName}</h2>

      {/* Filters */}
      <div style={{ marginBottom: "10px" }}>
        {routes.length > 0 && (
          <select
            value={selectedRoute}
            onChange={(e) => setSelectedRoute(e.target.value)}
            style={{ marginRight: "10px", padding: "6px" }}
          >
            <option value="">All Routes</option>
            {routes.map((r, i) => (
              <option key={i} value={r}>
                {r}
              </option>
            ))}
          </select>
        )}

        <label>From: </label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={{ marginRight: "10px" }}
        />
        <label>To: </label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={{ marginRight: "10px" }}
        />
      </div>

      {/* Table */}
      {customerData.length === 0 ? (
        <p>No data found.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table border="1" cellPadding="6" cellSpacing="0" style={{ width: "100%", textAlign: "center" }}>
            <thead>
              <tr>
                <th>Name</th>
                {products.map((p, i) => (
                  <th key={i}>{shortName(p)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customerData.map((c, idx) => (
                <tr key={idx}>
                  <td>{c.name}</td>
                  {products.map((_, i) => (
                    <td key={i}>
                      {c.totals[i] > 0 || c.schemes[i] > 0
                        ? `${c.totals[i]}/${c.schemes[i]}`
                        : "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button onClick={() => navigate(-1)} style={{ marginTop: "10px" }}>
        Back
      </button>
    </div>
  );
}
